import ArrowForward from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ContactUs from 'pages/LandingPage/components/ContactUs';
import Footer from 'pages/LandingPage/components/Footer';
import Navigation from 'pages/LandingPage/components/Navigation';
import type React from 'react';
import { useNavigate } from 'react-router-dom';

const pompeyoCarrascoZeller = '/landing/caseStudies/pompeyoxzeller.png';
const salfaZeller = '/landing/caseStudies/salfaxzeller.png';

const CompanyLogo = ({ company, logo }: { company: string; logo: string }) => (
  <Box
    sx={{
      width: '100%',
      height: 240,
      background: 'linear-gradient(135deg, #15183A 0%, #1e2147 100%)',
      borderRadius: '16px 16px 0 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '20px',
      fontWeight: 'bold',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        opacity: 0.3,
      },
    }}
  >
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {logo ? (
        <img
          src={logo}
          alt={company}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white' }}>
          {company}
        </Typography>
      )}
    </Box>
  </Box>
);

interface SuccessStoryCard {
  id: number;
  company: string;
  date: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  logo: string;
}

const SuccessStories: React.FC = () => {
  const navigate = useNavigate();

  const successStories: SuccessStoryCard[] = [
    {
      id: 2,
      company: 'Salfa',
      date: '19/6/2025',
      title: 'Cómo Salfa mejoró su eficiencia y aumentó sus tasas de cierre.',
      description:
        'Una historia de transformación digital exitosa en el sector automotriz a través de procesos automatizados.',
      category: 'Automotriz',
      readTime: '5 min',
      logo: salfaZeller,
    },
    {
      id: 1,
      company: 'Pompeyo Carrasco',
      date: '22/4/2025',
      title: 'Cómo Pompeyo Carrasco aumentó en un 200% su tasa de cierre.',
      description: 'Cómo Pompeyo Carrasco aumentó en un 200% su tasa de cierre.',
      category: 'Automotriz',
      readTime: '8 min',
      logo: pompeyoCarrascoZeller,
    },
  ];

  return (
    <>
      <Navigation />
      {/* Success Stories Section */}
      <Box sx={{ pt: 20, pb: 0, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {successStories.map((story) => (
              <Grid size={{ xs: 12, md: 6 }} key={story.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    border: 'none',
                    boxShadow: '0 8px 32px rgba(21, 24, 58, 0.08)',
                    transition: 'all 0.3s ease-in-out',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 48px rgba(21, 24, 58, 0.12)',
                    },
                  }}
                  onClick={() => navigate(`/casos-de-exito/${story.id}`)}
                >
                  <CompanyLogo company={story.company} logo={story.logo} />

                  <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Chip
                        label={story.category}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(249, 208, 10, 0.15)',
                          color: '#15183A',
                          fontWeight: 'medium',
                          fontSize: '11px',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {story.readTime} de lectura
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 'bold',
                        lineHeight: 1.3,
                        color: '#15183A',
                        mb: 2,
                        minHeight: '3.6em',
                      }}
                    >
                      {story.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mb: 3,
                        lineHeight: 1.6,
                        flexGrow: 1,
                      }}
                    >
                      {story.description}
                    </Typography>

                    <Box sx={{ mt: 'auto' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {story.date}
                        </Typography>

                        <Button
                          endIcon={<ArrowForward />}
                          sx={{
                            color: '#15183A',
                            fontWeight: 'medium',
                            fontSize: '14px',
                            '&:hover': {
                              backgroundColor: 'rgba(21, 24, 58, 0.04)',
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/casos-de-exito/${story.id}`);
                          }}
                        >
                          Leer caso
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* Call to Action */}
      <ContactUs />
      <Footer />
    </>
  );
};

export default SuccessStories;
